import { useState, useEffect } from 'react';
import { getActiveProcesses, dismissProcess, clearCompletedProcesses } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Loader2, 
  CheckCircle2, 
  XCircle, 
  X, 
  ChevronDown, 
  ChevronUp,
  RefreshCw,
  FileAudio,
  Clock,
  Trash2
} from 'lucide-react';
import { cn } from '@/lib/utils';

const PROCESS_ICONS = {
  reprocess_call: RefreshCw,
  regenerate_transcript: FileAudio,
  refresh_call_type: RefreshCw,
  sync_onedrive: RefreshCw,
  upload_call: FileAudio,
  batch_process: RefreshCw,
  duplicate_cleanup: Trash2,
  user_sync: RefreshCw,
  export_data: FileAudio
};

const STATUS_COLORS = {
  pending: 'bg-gray-100 text-gray-700',
  running: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-500'
};

const ProcessItem = ({ process, onDismiss }) => {
  const Icon = PROCESS_ICONS[process.process_type] || RefreshCw;
  const isActive = process.status === 'running' || process.status === 'pending';
  
  return (
    <div className={cn(
      "flex items-start gap-3 p-3 rounded-lg transition-all",
      process.status === 'running' && "bg-blue-50 dark:bg-blue-900/20",
      process.status === 'completed' && "bg-green-50 dark:bg-green-900/20 opacity-80",
      process.status === 'failed' && "bg-red-50 dark:bg-red-900/20",
      process.status === 'pending' && "bg-gray-50 dark:bg-gray-800/50"
    )}>
      <div className="flex-shrink-0 mt-0.5">
        {process.status === 'running' ? (
          <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
        ) : process.status === 'completed' ? (
          <CheckCircle2 className="h-5 w-5 text-green-600" />
        ) : process.status === 'failed' ? (
          <XCircle className="h-5 w-5 text-red-600" />
        ) : (
          <Clock className="h-5 w-5 text-gray-400" />
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-medium truncate">{process.title}</p>
          <Badge className={cn("text-xs", STATUS_COLORS[process.status])}>
            {process.status}
          </Badge>
        </div>
        
        <p className="text-xs text-muted-foreground mt-0.5">
          {process.progress_message}
        </p>
        
        {isActive && (
          <Progress 
            value={process.progress_current} 
            className="h-1.5 mt-2" 
          />
        )}
        
        {process.result_summary && process.status === 'completed' && (
          <p className="text-xs text-green-700 dark:text-green-400 mt-1">
            {process.result_summary}
          </p>
        )}
        
        {process.error_message && process.status === 'failed' && (
          <p className="text-xs text-red-600 dark:text-red-400 mt-1">
            {process.error_message}
          </p>
        )}
      </div>
      
      {!isActive && (
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-6 w-6 flex-shrink-0"
          onClick={() => onDismiss(process.id)}
        >
          <X className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
};

export default function ProcessTracker() {
  const [processes, setProcesses] = useState([]);
  const [isExpanded, setIsExpanded] = useState(true);
  const [hasActive, setHasActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const fetchProcesses = async () => {
    try {
      const { data } = await getActiveProcesses(true);
      setProcesses(data.processes || []);
      setHasActive(data.has_active);
      
      // Auto-expand if there are active processes
      if (data.has_active && !isExpanded) {
        setIsExpanded(true);
      }
    } catch (error) {
      console.error('Failed to fetch processes:', error);
    }
  };

  useEffect(() => {
    fetchProcesses();
    
    // Poll for updates every 3 seconds if there are active processes
    const interval = setInterval(() => {
      fetchProcesses();
    }, 3000);
    
    return () => clearInterval(interval);
  }, []);

  const handleDismiss = async (processId) => {
    try {
      await dismissProcess(processId);
      setProcesses(prev => prev.filter(p => p.id !== processId));
    } catch (error) {
      console.error('Failed to dismiss process:', error);
    }
  };

  const handleClearAll = async () => {
    setIsLoading(true);
    try {
      await clearCompletedProcesses();
      await fetchProcesses();
    } catch (error) {
      console.error('Failed to clear processes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Don't render if no processes
  if (processes.length === 0) {
    return null;
  }

  const activeCount = processes.filter(p => p.status === 'running' || p.status === 'pending').length;
  const completedCount = processes.filter(p => p.status === 'completed' || p.status === 'failed').length;

  return (
    <div className="fixed bottom-4 right-4 z-50 w-96 max-w-[calc(100vw-2rem)]">
      <Card className="shadow-lg border-2">
        {/* Header */}
        <div 
          className={cn(
            "flex items-center justify-between px-4 py-2 cursor-pointer border-b",
            hasActive ? "bg-blue-50 dark:bg-blue-900/20" : "bg-gray-50 dark:bg-gray-800/50"
          )}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-2">
            {hasActive && <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />}
            <span className="font-medium text-sm">
              {hasActive ? `${activeCount} Process${activeCount !== 1 ? 'es' : ''} Running` : 'Recent Processes'}
            </span>
            {completedCount > 0 && (
              <Badge variant="secondary" className="text-xs">
                {completedCount} done
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            {completedCount > 0 && (
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-6 w-6"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearAll();
                }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Trash2 className="h-3 w-3" />
                )}
              </Button>
            )}
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronUp className="h-4 w-4" />
            )}
          </div>
        </div>
        
        {/* Content */}
        {isExpanded && (
          <CardContent className="p-2 max-h-80 overflow-y-auto">
            <div className="space-y-2">
              {processes.map(process => (
                <ProcessItem 
                  key={process.id} 
                  process={process} 
                  onDismiss={handleDismiss}
                />
              ))}
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
